import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DistributionCard, Skeleton } from '@hotelise/ui';
import type { DistributionItem } from '@hotelise/ui';

import {
	getDashboardReportData,
	getDashboardReportLoading,
} from 'state/modules/dashboard';
import { CHANNEL_NAMES } from 'interfaces/channels';

const CHANNEL_CONFIG: Record<string, { label: string; color: string }> = {
	[CHANNEL_NAMES.AIRBNB_OFFICIAL]: { label: 'Airbnb', color: 'bg-[hsl(350,80%,60%)]' },
	[CHANNEL_NAMES.AIRBNB]: { label: 'Airbnb', color: 'bg-[hsl(350,80%,60%)]' },
	[CHANNEL_NAMES.BOOKING]: { label: 'Booking.com', color: 'bg-[hsl(210,80%,55%)]' },
	[CHANNEL_NAMES.VRBO]: { label: 'VRBO', color: 'bg-[hsl(45,90%,55%)]' },
	[CHANNEL_NAMES.MANUAL]: { label: 'Other', color: 'bg-[hsl(160,50%,50%)]' },
	[CHANNEL_NAMES.DIRECT]: { label: 'Other', color: 'bg-[hsl(160,50%,50%)]' },
};

const ChannelDistribution = () => {
	const report = useSelector(getDashboardReportData);
	const isLoading = useSelector(getDashboardReportLoading);

	const items = useMemo((): DistributionItem[] => {
		const channelDistribution = report?.calculations?.channelDistribution;
		if (!channelDistribution) return [];

		const merged: Record<string, number> = {};

		for (const [channel, count] of Object.entries(channelDistribution)) {
			const config = CHANNEL_CONFIG[channel];
			const label = config?.label ?? 'Other';
			merged[label] = (merged[label] || 0) + count;
		}

		const colorByLabel: Record<string, string> = {};
		for (const config of Object.values(CHANNEL_CONFIG)) {
			colorByLabel[config.label] = config.color;
		}

		return Object.entries(merged)
			.map(([name, value]) => ({
				name,
				value,
				color: colorByLabel[name] || 'bg-[hsl(160,50%,50%)]',
			}))
			.filter((item) => item.value > 0)
			.sort((a, b) => b.value - a.value);
	}, [report]);

	if (isLoading) {
		return (
			<div className="bg-card rounded-lg p-4 border">
				<Skeleton className="h-5 w-36 mb-2" />
				<Skeleton className="h-8 w-24 mb-3" />
				<Skeleton className="h-2 w-full rounded-full mb-3" />
				<div className="space-y-2">
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-3/4" />
				</div>
			</div>
		);
	}

	return (
		<DistributionCard
			title="Channel Distribution"
			items={items}
			unit="reservations"
		/>
	);
};

export default ChannelDistribution;
